/**
 * @file: MessageListFlatList.tsx
 *
 * The inverted message list (newest-first; the visual bottom is offset 0),
 * backed by React Native's `FlatList`. The original/fallback backend — see
 * `MessageList.tsx` for the backend selector.
 *
 * Scrolling up toward older history reaches the END of the inverted list, so
 * `onEndReached` drives the older-page fetch.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMsg, keyOfChatMsg } from '@euler/model/chat';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, RefObject, memo } from 'react';
import { FlatList } from 'react-native';
import {
  LoadingOlderFooter,
  MESSAGE_LIST_BG,
  MessageListProps,
} from './messageListShared';

export const MessageListFlatList: FC<MessageListProps> = memo(
  ({
    messages,
    renderItem,
    flatListRef,
    onScroll,
    onScrollToIndexFailed,
    onEndReached,
    loadingOlder$,
    style,
  }) => {
    const { rpx } = useResponsiveContext();
    const [loadingOlder] = useBehaviorSubject(loadingOlder$);
    return (
      <FlatList
        ref={flatListRef as unknown as RefObject<FlatList<ChatMsg>>}
        data={messages}
        inverted
        keyExtractor={keyOfChatMsg}
        renderItem={renderItem}
        style={[{ backgroundColor: MESSAGE_LIST_BG }, style]}
        contentContainerStyle={{
          padding: rpx(12),
          // Inverted list lays out column-reverse, so its main-axis start is
          // the visual bottom. With few messages flexGrow fills the height
          // and `flex-end` packs them toward the inverted end = the visual
          // TOP. Once messages exceed the viewport this is a no-op.
          flexGrow: 1,
          justifyContent: 'flex-end',
        }}
        onScroll={onScroll}
        onScrollToIndexFailed={onScrollToIndexFailed}
        onEndReached={onEndReached}
        onEndReachedThreshold={0.3}
        ListFooterComponent={loadingOlder ? <LoadingOlderFooter /> : null}
        scrollEventThrottle={100}
      />
    );
  },
);
